import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";
import { useCart } from "../lib/cart";
import { useCurrency } from "../lib/currency";
import { AnimatePresence, motion, scrim } from "../lib/motion";

/**
 * Slide-over confirmation of what was just added. Secondary to the cart page —
 * it never blocks the flow, closes on Escape, restores focus on close, and
 * dismisses itself after a few seconds unless the pointer or keyboard is in it.
 */
export function MiniCart() {
  const cart = useCart();
  const { format } = useCurrency();
  const panelRef = useRef(null);
  const returnTo = useRef(null);
  const timer = useRef(0);
  const open = cart.drawer;
  const item = cart.lastItem;

  useEffect(() => {
    if (!open) return undefined;
    returnTo.current = document.activeElement;
    const t = setTimeout(() => panelRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") cart.closeDrawer(); };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      if (returnTo.current && returnTo.current.focus) returnTo.current.focus();
    };
  }, [open, cart]);

  const arm = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => cart.closeDrawer(), 7000);
  };
  const hold = () => clearTimeout(timer.current);

  useEffect(() => {
    if (open) arm();
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div key="mini-cart" className="fixed inset-0 z-[60]" data-testid="mini-cart">
          <motion.div
            variants={scrim}
            initial="hidden"
            animate="show"
            exit="exit"
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={cart.closeDrawer}
          />
          <motion.aside
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-label="Added to cart"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
            onMouseEnter={hold}
            onFocus={hold}
            onMouseLeave={arm}
            className="absolute right-0 top-0 flex h-full w-full max-w-[400px] flex-col border-l border-rule bg-surface"
          >
            <div className="flex items-center justify-between border-b border-rule px-6 py-5">
              <p className="ui flex items-center gap-2 text-sm font-medium text-ink">
                <ShoppingBag className="h-4 w-4 text-gold" aria-hidden="true" />
                Added to your cart
              </p>
              <button
                type="button"
                onClick={cart.closeDrawer}
                aria-label="Close"
                data-testid="mini-cart-close"
                className="text-dim transition-colors hover:text-gold"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {item ? (
                <div className="flex gap-4" data-testid="mini-cart-item">
                  <img
                    src={item.image}
                    alt=""
                    className="h-24 w-24 shrink-0 border border-rule object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-base leading-tight">{item.title}</p>
                    <p className="ui mt-1 text-xs text-dim">{item.subtitle}</p>
                    <p className="num mt-2 text-sm text-deep">{format(item.price_inr)}</p>
                    {item.setting && (
                      <p className="ui mt-1 text-xs text-dim">
                        Setting: {item.setting}
                        {item.ring_size ? ` · size ${item.ring_size}` : ""}
                      </p>
                    )}
                    {(item.addons || []).length > 0 && (
                      <ul className="ui mt-2 space-y-0.5 border-l border-rule pl-2" data-testid="mini-cart-addons">
                        {item.addons.map((a) => (
                          <li key={a.id} className="flex items-center justify-between gap-2 text-[11px] text-dim">
                            <span>+ {a.label}</span>
                            <span className="num text-ink">{format(a.price_inr)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="ui mt-2 text-[11px] text-verify">
                      One of one — held for you while it is in your cart.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="ui text-sm text-dim">Your cart is empty.</p>
              )}
            </div>

            <div className="border-t border-rule px-6 py-6">
              <div className="flex items-baseline justify-between">
                <p className="ui text-sm text-dim">
                  Subtotal · {cart.count} item{cart.count === 1 ? "" : "s"}
                </p>
                <p className="num text-lg text-deep" data-testid="mini-cart-subtotal">
                  {format(cart.goodsTotal)}
                </p>
              </div>
              <p className="ui mt-2 text-xs leading-[1.6] text-dim" data-testid="mini-cart-shipping-note">
                Shipping and any duties are calculated at checkout before you pay.
              </p>
              <div className="mt-5 flex gap-3">
                <Link
                  to="/cart"
                  onClick={cart.closeDrawer}
                  className="btn btn-secondary flex-1"
                  data-testid="mini-cart-view"
                >
                  View cart
                </Link>
                <Link
                  to="/checkout"
                  onClick={cart.closeDrawer}
                  className="btn btn-primary flex-1"
                  data-testid="mini-cart-checkout"
                >
                  Checkout
                </Link>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
